'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { LocationPicker, type ChosenPlace } from '@/components/maps/LocationPicker';
import { nameFromPlace } from '@/components/maps/place-view';
import { DomainTab } from '@/components/platform/agency-domain';
import { DatabaseTab, SiteTab, StaffTab } from '@/components/platform/agency-operate';
import { Field, fieldClass } from '@/components/platform/agency-fields';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { FEATURE_CATALOG, groupFeatures, type Entitlements } from '@/lib/entitlements';
import { accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseCatalog } from '@/lib/platform/catalog';
import { parseTenantDetail, type PlatformTenantRow, type TenantDetail } from '@/lib/platform/summary';
const BILLING = ['trial', 'current', 'past_due', 'cancelled', 'expired'] as const;
const TABS = ['general', 'access', 'domain', 'site', 'staff', 'database'] as const;
type Tab = (typeof TABS)[number];


export function AgencyManagePanel({
  tenant,
  onChanged,
  onEnterDesk,
}: {
  tenant: PlatformTenantRow;
  onChanged: () => void;
  onEnterDesk: () => void;
}) {
  const [tab, setTab] = useState<Tab>('general');
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<unknown>(`/api/platform/tenants/${tenant.slug}`)
      .then((payload) => {
        if (!cancelled) {
          setDetail(parseTenantDetail(payload));
          setDetailError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && !leaveIfSignedOut(err)) {
          setDetail(null);
          setDetailError(accountError(err, 'Could not open this agency.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenant.slug]);

  function reloadDetail() {
    api<unknown>(`/api/platform/tenants/${tenant.slug}`)
      .then((payload) => setDetail(parseTenantDetail(payload)))
      .catch((err: unknown) => {
        if (!leaveIfSignedOut(err)) setDetailError(accountError(err, 'Could not open this agency.'));
      });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">{tenant.city} · {tenant.slug}</p>
        <button
          type="button"
          disabled={tenant.error !== null}
          title={tenant.error ?? undefined}
          onClick={onEnterDesk}
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50"
        >
          Enter desk
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setTab(item)}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${tab === item ? 'bg-brand text-slate-950' : 'border border-line text-ink'}`}
          >
            {item === 'site' ? 'Website' : item}
          </button>
        ))}
      </div>
      {detailError && <p className="text-sm text-danger">{detailError}</p>}
      {tab === 'general' && <GeneralTab tenant={tenant} detail={detail} onChanged={onChanged} />}
      {tab === 'access' && <AccessTab slug={tenant.slug} entitlements={detail?.entitlements ?? null} onSaved={reloadDetail} />}
      {tab === 'domain' && <DomainTab slug={tenant.slug} publishedAt={tenant.storefrontPublishedAt} />}
      {tab === 'staff' && <StaffTab slug={tenant.slug} detail={detail} onChanged={reloadDetail} />}
      {tab === 'site' && <SiteTab slug={tenant.slug} closed={tenant.error !== null} />}
      {tab === 'database' && <DatabaseTab slug={tenant.slug} onRepaired={onChanged} />}
    </div>
  );
}

function GeneralTab({
  tenant,
  detail,
  onChanged,
}: {
  tenant: PlatformTenantRow;
  detail: TenantDetail | null;
  onChanged: () => void;
}) {
  return (
    <div className="space-y-6">
      <ProfileForm key={detail?.profile?.email ?? tenant.slug} tenant={tenant} detail={detail} onChanged={onChanged} />
      <PlanForm key={planKey(tenant)} tenant={tenant} onSaved={onChanged} />
    </div>
  );
}

function ProfileForm({
  tenant,
  detail,
  onChanged,
}: {
  tenant: PlatformTenantRow;
  detail: TenantDetail | null;
  onChanged: () => void;
}) {
  const profile = detail?.profile;
  const [name, setName] = useState(profile?.name ?? tenant.name);
  const [city, setCity] = useState(profile?.city ?? tenant.city);
  const [address, setAddress] = useState(profile?.address ?? '');
  const [latitude, setLatitude] = useState<number | null>(detail?.latitude ?? tenant.latitude);
  const [longitude, setLongitude] = useState<number | null>(detail?.longitude ?? tenant.longitude);
  const [email, setEmail] = useState(profile?.email ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [ice, setIce] = useState(profile?.ice ?? '');
  const [rc, setRc] = useState(profile?.rc ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filledName = useRef('');
  const closed = detail?.error !== null && detail !== null;

  async function save(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.slug}/profile`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: name.trim(),
          city: city.trim(),
          ...(address.trim() !== '' ? { address: address.trim() } : {}),
          email: email.trim(),
          phone: phone.trim(),
          ice: ice.trim(),
          rc: rc.trim(),
          ...(latitude !== null && longitude !== null ? { latitude, longitude } : {}),
        }),
      });
      notify('Profile saved.');
      onChanged();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not save this profile.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void save(event)} className="space-y-3">
      <h3 className="text-sm font-semibold text-ink">General and quotas</h3>
      {closed && <p className="text-xs text-danger">{detail?.error}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Legal name" value={name} onChange={setName} />
        <div className="sm:col-span-2">
          <LocationPicker
            value={latitude === null || longitude === null ? null : { name, city, address, country: '', latitude, longitude }}
            onChange={(place: ChosenPlace) => {
              const street = place.address.trim() === '' ? place.city : place.address;
              const incoming = nameFromPlace(name, filledName.current, place.name);
              if (incoming !== null) {
                const nextName = incoming.slice(0, 120);
                filledName.current = nextName;
                setName(nextName);
              }
              setCity(place.city.slice(0, 80));
              setAddress(street.slice(0, 500));
              setLatitude(place.latitude);
              setLongitude(place.longitude);
            }}
            hint="Choose a place on the map. Each choice replaces the legal name, city, and address."
            lookingLabel="Reading this point…"
            failedLabel="This point could not be read. Try another."
          />
        </div>
        <Field label="Email" value={email} onChange={setEmail} type="email" />
        <Field label="Phone" value={phone} onChange={setPhone} />
        <Field label="ICE" value={ice} onChange={setIce} />
        <Field label="RC" value={rc} onChange={setRc} />
      </div>
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      <button type="submit" disabled={pending || closed} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
        {pending ? 'Saving…' : 'Save profile'}
      </button>
    </form>
  );
}


function AccessTab({
  slug,
  entitlements,
  onSaved,
}: {
  slug: string;
  entitlements: Entitlements | null;
  onSaved: () => void;
}) {
  const [extra, setExtra] = useState<string[]>(entitlements?.extraKeys ?? []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setExtra(entitlements?.extraKeys ?? []);
  }, [entitlements]);

  if (!entitlements) {
    return <p className="text-sm text-muted">Access details are still loading.</p>;
  }

  const catalog = entitlements.catalog.length > 0 ? entitlements.catalog : FEATURE_CATALOG;
  const planKeys = entitlements.planKeys;
  const effective = new Set([...planKeys, ...extra]);

  function toggle(key: string) {
    if (planKeys.includes(key)) return;
    setExtra((current) => (
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    ));
  }

  async function save() {
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/tenants/${slug}/entitlements`, {
        method: 'PUT',
        body: JSON.stringify({ extra }),
      });
      notify('Access saved.');
      onSaved();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not save access.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-line bg-shell px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Current plan</p>
        <p className="mt-0.5 text-sm font-bold text-ink">{entitlements.plan.name}</p>
        <p className="mt-1 text-xs text-muted">
          {planKeys.length} included · {extra.length} extra · {effective.size} effective
        </p>
      </div>
      <p className="text-xs leading-relaxed text-muted">
        Plan modules are always on. Tick an extra module to grant it to this agency on top of its plan; untick to remove
        it. Extra access is audited and survives plan changes.
      </p>
      <div className="space-y-3">
        {groupFeatures(catalog).map(({ group, features }) => (
          <div key={group}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{group}</p>
            <div className="mt-1.5 space-y-1.5">
              {features.map((feature) => {
                const included = planKeys.includes(feature.key);
                const granted = extra.includes(feature.key);
                return (
                  <label
                    key={feature.key}
                    className={`flex items-start gap-2 rounded-xl border border-line px-2.5 py-2 text-xs ${included ? 'bg-shell' : 'bg-card'}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-brand"
                      checked={included || granted}
                      disabled={included || pending}
                      onChange={() => toggle(feature.key)}
                    />
                    <span className="flex-1">
                      <span className="font-semibold text-ink">{feature.label}</span>
                      <span className="mt-0.5 block text-[10px] leading-snug text-muted">{feature.description}</span>
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        included ? 'bg-brand/15 text-brand' : granted ? 'bg-success/15 text-success' : 'bg-shell text-muted'
                      }`}
                    >
                      {included ? 'Plan' : granted ? 'Extra' : 'Off'}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      <button
        type="button"
        disabled={pending}
        onClick={() => void save()}
        className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50"
      >
        {pending ? 'Saving…' : 'Save access'}
      </button>
    </div>
  );
}

function PlanForm({ tenant, onSaved }: { tenant: PlatformTenantRow; onSaved: () => void }) {
  const [tier, setTier] = useState(tenant.subscriptionTier);
  const [planOptions, setPlanOptions] = useState<string[]>([tenant.subscriptionTier]);
  const [billing, setBilling] = useState<(typeof BILLING)[number]>(readBilling(tenant.billingStatus));
  const [trialEndsAt, setTrialEndsAt] = useState(tenant.trialEndsAt ?? '');
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState(tenant.subscriptionEndsAt ?? '');
  const [vehicleLimit, setVehicleLimit] = useState(tenant.vehicleLimit === null ? '' : String(tenant.vehicleLimit));
  const [staffLimit, setStaffLimit] = useState(tenant.staffLimit === null ? '' : String(tenant.staffLimit));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/platform/plans')
      .then((payload) => {
        if (cancelled) return;
        const active = parseCatalog(payload).filter((plan) => plan.active).map((plan) => plan.id);
        setPlanOptions(active.includes(tenant.subscriptionTier) ? active : [tenant.subscriptionTier, ...active]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenant.subscriptionTier]);

  async function save(event: FormEvent) {
    event.preventDefault();
    const vehicles = wholeNumber(vehicleLimit, 'Vehicle cap');
    const staff = wholeNumber(staffLimit, 'Staff cap');
    if (typeof vehicles === 'string') {
      setError(vehicles);
      return;
    }
    if (typeof staff === 'string') {
      setError(staff);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/tenants/${tenant.slug}`, {
        method: 'PATCH',
        body: JSON.stringify({
          subscriptionTier: tier,
          billingStatus: billing,
          trialEndsAt: trialEndsAt || null,
          subscriptionEndsAt: subscriptionEndsAt || null,
          vehicleLimit: vehicles,
          staffLimit: staff,
        }),
      });
      notify('Plan saved.');
      onSaved();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not save this plan.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void save(event)} className="space-y-3 border-t border-line pt-4">
      <h3 className="text-sm font-semibold text-ink">Plan and fleet cap</h3>
      <p className="text-xs text-muted">A blank cap means unlimited. Saving a plan does not charge a card.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Choice label="Plan" value={tier} options={planOptions} onChange={setTier} />
        <Choice label="Billing" value={billing} options={BILLING} onChange={(value) => setBilling(readBilling(value))} />
        <label className="text-xs font-medium text-muted">Trial ends<input type="date" value={trialEndsAt} onChange={(event) => setTrialEndsAt(event.target.value)} className={fieldClass} /></label>
        <label className="text-xs font-medium text-muted">Subscription ends<input type="date" value={subscriptionEndsAt} onChange={(event) => setSubscriptionEndsAt(event.target.value)} className={fieldClass} /></label>
        <label className="text-xs font-medium text-muted">Vehicle cap<input inputMode="numeric" value={vehicleLimit} onChange={(event) => setVehicleLimit(event.target.value)} placeholder="Unlimited" className={fieldClass} /></label>
        <label className="text-xs font-medium text-muted">Staff cap<input inputMode="numeric" value={staffLimit} onChange={(event) => setStaffLimit(event.target.value)} placeholder="Unlimited" className={fieldClass} /></label>
      </div>
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      <button type="submit" disabled={pending} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
        {pending ? 'Saving…' : 'Save plan'}
      </button>
    </form>
  );
}

function Choice({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return (
    <label className="text-xs font-medium text-muted">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={`${fieldClass} capitalize`}>
        {options.map((option) => <option key={option} value={option}>{option.replaceAll('_', ' ')}</option>)}
      </select>
    </label>
  );
}

function planKey(tenant: PlatformTenantRow): string {
  return [tenant.subscriptionTier, tenant.billingStatus, tenant.vehicleLimit, tenant.staffLimit, tenant.customDomainAllowed].join('|');
}

function wholeNumber(value: string, label: string): number | null | string {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return `${label} must be a whole number, or blank for unlimited.`;
  return Number(trimmed);
}

function readBilling(value: string): (typeof BILLING)[number] {
  switch (value) {
    case 'trial':
    case 'current':
    case 'past_due':
    case 'cancelled':
    case 'expired':
      return value;
    default:
      return 'current';
  }
}

